'use client';

import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';

const tiers = [
  {
    name: 'Starter',
    price: 49,
    annualPrice: 41,
    properties: '1-5',
    seats: '2 seats',
    description: 'For small landlords getting organized.',
    features: [
      'Work orders & tracking',
      'Basic asset management',
      'Tenant portal',
      'Preventive maintenance',
      'Email notifications',
      'Document vault',
    ],
    highlighted: false,
  },
  {
    name: 'Growth',
    price: 129,
    annualPrice: 107,
    properties: '6-25',
    seats: '5 seats',
    description: 'For growing portfolios with vendor needs.',
    features: [
      'Everything in Starter',
      'Vendor portal & management',
      'Owner visibility portal',
      'Reporting & analytics',
      'Inspection workflows',
      'Priority support',
    ],
    highlighted: false,
  },
  {
    name: 'Professional',
    price: 249,
    annualPrice: 207,
    properties: '26-100',
    seats: '10 seats',
    description: 'For established managers who need advanced tools.',
    features: [
      'Everything in Growth',
      'Advanced analytics',
      'API access',
      'Custom workflows',
      'Bulk operations',
      'Tax Center eligible ($59/mo add-on)',
    ],
    highlighted: true,
    badge: 'MOST POPULAR',
  },
  {
    name: 'Business',
    price: 499,
    annualPrice: 415,
    properties: '100+',
    seats: '25 seats',
    description: 'For large operations with enterprise needs.',
    features: [
      'Everything in Professional',
      'Tax Center included',
      'White-label option',
      'Dedicated account manager',
      'Custom integrations',
      'SLA guarantee',
    ],
    highlighted: false,
  },
];

const Pricing = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="pricing"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px]">
          <div className="absolute inset-0 bg-gradient-radial from-primary-500/[0.06] to-transparent rounded-full blur-3xl" />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span
            className={cn(
              "inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase",
              "bg-primary-500/10 text-primary-600 dark:text-primary-400 border border-primary-500/20 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
          >
            Transparent Pricing
          </span>
          <h2
            className={cn(
              "font-playfair text-4xl md:text-5xl font-bold text-gray-900 dark:text-secondary-100 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '100ms' }}
          >
            Plans That Scale
            <br />
            <span className="text-primary-600 dark:text-primary-400">With Your Portfolio</span>
          </h2>
          <p
            className={cn(
              "max-w-xl mx-auto text-lg text-gray-500 dark:text-secondary-400 mb-8",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '200ms' }}
          >
            Launching Q3 2026. Join the waitlist for early access and a locked-in launch rate.
          </p>

          {/* Billing Toggle */}
          <div
            className={cn(
              "inline-flex items-center gap-4 p-1.5 rounded-xl bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '300ms' }}
          >
            <button
              onClick={() => setIsAnnual(false)}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                !isAnnual ? "bg-primary-500 text-white" : "text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200"
              )}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2",
                isAnnual ? "bg-primary-500 text-white" : "text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200"
              )}
            >
              Annual
              <span className={cn(
                "text-xs px-2 py-0.5 rounded-full",
                isAnnual ? "bg-white/20 text-white" : "bg-primary-500/15 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400"
              )}>
                Save ~17%
              </span>
            </button>
          </div>
        </div>

        {/* Tier Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {tiers.map((tier, i) => (
            <div
              key={tier.name}
              className={cn(
                "relative rounded-2xl p-6 flex flex-col",
                tier.highlighted
                  ? "bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a1a1a] dark:to-[#141414] border-2 border-primary-500/40"
                  : "bg-white shadow-sm dark:bg-white/[0.03] dark:shadow-none border border-gray-200 dark:border-white/[0.06]",
                "transition-all duration-500",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: `${400 + i * 75}ms` }}
            >
              {tier.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-3 py-1 bg-primary-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                    {tier.badge}
                  </span>
                </div>
              )}

              <div className="mb-5">
                <h3 className="text-xl font-bold text-gray-900 dark:text-secondary-100 mb-1">{tier.name}</h3>
                <p className="text-xs text-gray-400 dark:text-secondary-500">{tier.properties} properties &middot; {tier.seats}</p>
              </div>

              <div className="mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-gray-900 dark:text-secondary-100 font-playfair">
                    ${isAnnual ? tier.annualPrice : tier.price}
                  </span>
                  <span className="text-gray-400 dark:text-secondary-500 text-sm">/mo</span>
                </div>
                {isAnnual && (
                  <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                    ${tier.annualPrice * 12}/year (save ${(tier.price - tier.annualPrice) * 12}/yr)
                  </p>
                )}
              </div>

              <p className="text-sm text-gray-500 dark:text-secondary-400 mb-5">{tier.description}</p>

              <div className="space-y-2.5 mb-6 flex-grow">
                {tier.features.map((feature, j) => (
                  <div key={j} className="flex items-start gap-2.5">
                    <Check className={cn(
                      "w-4 h-4 flex-shrink-0 mt-0.5",
                      tier.highlighted ? "text-primary-600 dark:text-primary-400" : "text-gray-400 dark:text-secondary-500"
                    )} />
                    <span className="text-sm text-gray-600 dark:text-secondary-300">{feature}</span>
                  </div>
                ))}
              </div>

              <a
                href="#contact"
                className={cn(
                  "w-full inline-flex items-center justify-center gap-2 py-3 px-5 rounded-xl text-sm font-semibold transition-all duration-300",
                  tier.highlighted
                    ? "bg-primary-500 text-white hover:bg-primary-400 shadow-lg dark:shadow-[0_0_30px_-8px_rgba(59,130,246,0.4)]"
                    : "bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-700 dark:text-secondary-200 hover:bg-gray-50 dark:hover:bg-white/[0.08]"
                )}
              >
                Join the Waitlist
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>

        {/* CoreOps Cross-link */}
        <div
          className={cn(
            "max-w-2xl mx-auto p-6 rounded-2xl bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] text-center",
            "transition-all duration-700",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
          style={{ transitionDelay: '700ms' }}
        >
          <p className="text-gray-500 dark:text-secondary-400 text-sm mb-2">
            <span className="text-gray-700 dark:text-secondary-200 font-medium">Don't want to self-manage?</span>
          </p>
          <p className="text-gray-400 dark:text-secondary-500 text-sm mb-4">
            CoreOps handles everything for you — dedicated coordinator, vendor management, and 24/7 emergency response.
          </p>
          <a
            href="https://coreops-landing.vercel.app"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
          >
            Learn about CoreOps Managed Service
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export { Pricing };
